import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, number, thaiDateTime } from '../lib/format'

/**
 * Scenario planning (part 104). The page does not calculate anything: the server reads the
 * issued invoices, builds one actuals base and stores base/upside/downside runs plus a
 * +/-10% churn sensitivity table. Rendering a stored snapshot means the runway on screen is the
 * runway a second reviewer sees after new invoices arrive.
 */

type ScenarioKind = 'base' | 'upside' | 'downside'

interface ScenarioModel {
  id: string
  name: string
  as_of_month: string
  history_from: string
  history_to: string
  history_months: number
  invoice_count: number
  months_with_invoices: number
  base_mrr_satang: number
  actual_total_satang: number
  starting_cash_satang: number
  fixed_cost_satang: number
  variable_cost_bp: number
  monthly_churn_bp: number
  monthly_expansion_bp: number
  horizon_months: number
  created_by?: string
  generated_at: string
}

interface ScenarioRun {
  id: string
  model_id: string
  model_name: string
  kind: ScenarioKind
  revenue_multiplier_bp: number
  churn_multiplier_bp: number
  expansion_multiplier_bp: number
  effective_churn_bp: number
  effective_expansion_bp: number
  starting_revenue_satang: number
  horizon_months: number
  total_revenue_satang: number
  total_fixed_cost_satang: number
  total_variable_cost_satang: number
  total_cost_satang: number
  total_net_cashflow_satang: number
  ending_cash_satang: number
  min_cash_satang: number
  first_month_net_satang: number
  breach_month: number
  runway_months: number
  runway_beyond_horizon: boolean
  generated_by?: string
  generated_at: string
}

interface ScenarioMonth {
  month_index: number
  month: string
  revenue_satang: number
  fixed_cost_satang: number
  variable_cost_satang: number
  total_cost_satang: number
  net_cashflow_satang: number
  opening_cash_satang: number
  closing_cash_satang: number
  churn_bp: number
  expansion_bp: number
}

interface ScenarioScenario {
  run: ScenarioRun
  months: ScenarioMonth[]
}

interface Sensitivity {
  kind: ScenarioKind
  churn_delta_bp: number
  effective_churn_bp: number
  effective_expansion_bp: number
  runway_months: number
  breach_month: number
  runway_beyond_horizon: boolean
  ending_cash_satang: number
  first_month_net_satang: number
  revenue_at_horizon_satang: number
}

interface ScenarioBundle {
  model: ScenarioModel
  scenarios: ScenarioScenario[]
  sensitivity: Sensitivity[]
}

const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

const KIND_ORDER: ScenarioKind[] = ['base', 'upside', 'downside']
const KIND_LABEL: Record<ScenarioKind, string> = {
  base: 'ฐาน',
  upside: 'ดีกว่าคาด',
  downside: 'แย่กว่าคาด',
}
const KIND_TONE: Record<ScenarioKind, string> = {
  base: 'var(--color-gold)',
  upside: 'var(--color-emerald-soft)',
  downside: 'var(--color-critical)',
}
const KIND_NOTE: Record<ScenarioKind, string> = {
  base: 'รายได้ ×1.00 · churn ×1.00 · expansion ×1.00',
  upside: 'รายได้ ×1.15 · churn ×0.70 · expansion ×1.35',
  downside: 'รายได้ ×0.85 · churn ×1.40 · expansion ×0.50',
}

const DEFAULT_FORM = {
  name: '',
  cash: '24000',
  fixed: '34500',
  variable: '20',
  churn: '2',
  expansion: '1',
  horizon: '12',
}

function fullMonth(month: string): string {
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  return `${TH_MONTHS[m - 1]} ${y + 543}`
}

function rateLabel(bp: number): string {
  return `${(bp / 100).toFixed(2)}%`
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

function shortID(id: string): string {
  return id.slice(0, 8)
}

function runwayText(run: ScenarioRun): string {
  if (run.runway_beyond_horizon) return `เกิน ${number(run.horizon_months)} เดือน`
  return `${number(run.runway_months)} เดือน`
}

function breachText(run: ScenarioRun): string {
  if (run.breach_month === 0) return 'ไม่ติดลบในช่วงที่จำลอง'
  return `เงินสดติดลบเดือนที่ ${number(run.breach_month)}`
}

function scenarioOf(bundle: ScenarioBundle | undefined, kind: ScenarioKind): ScenarioScenario | undefined {
  return bundle?.scenarios.find((item) => item.run.kind === kind)
}

function deltaLabel(delta: number): string {
  if (delta < 0) return 'ลด 10%'
  if (delta > 0) return 'เพิ่ม 10%'
  return 'ฐาน'
}

/** A small SVG chart of closing cash for the three scenarios. */
function CashChart({ bundle }: { bundle: ScenarioBundle }) {
  const series = bundle.scenarios.map((item) => ({
    kind: item.run.kind,
    months: item.months,
  }))
  const all = series.flatMap((item) => item.months.map((month) => month.closing_cash_satang))
  if (all.length === 0) return null

  const W = 860
  const H = 280
  const left = 68
  const right = 18
  const top = 20
  const bottom = 38
  const plotW = W - left - right
  const plotH = H - top - bottom
  const rawMax = Math.max(0, ...all)
  const rawMin = Math.min(0, ...all)
  const pad = Math.max(1, (rawMax - rawMin) * 0.08)
  const max = rawMax + pad
  const min = rawMin - pad
  const span = max - min || 1
  const monthCount = Math.max(0, ...series.map((item) => item.months.length))
  const x = (i: number) => left + (i * plotW) / Math.max(1, monthCount - 1)
  const y = (value: number) => top + plotH - ((value - min) / span) * plotH
  const zeroY = y(0)

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="กราฟเงินสดคงเหลือของสามสถานการณ์" style={{ width: '100%', height: 'auto' }}>
        {[0, 0.25, 0.5, 0.75, 1].map((share) => {
          const value = min + span * share
          const yy = y(value)
          return (
            <g key={share}>
              <line x1={left} y1={yy} x2={W - right} y2={yy} stroke="var(--color-hairline)" strokeDasharray="3 5" />
              <text x={left - 9} y={yy + 4} textAnchor="end" fontSize={10} fill="var(--color-ivory-dim)">
                {money(Math.round(value), { compact: true })}
              </text>
            </g>
          )
        })}
        {min < 0 && max > 0 ? (
          <line x1={left} y1={zeroY} x2={W - right} y2={zeroY} stroke="var(--color-critical)" strokeWidth={1} strokeDasharray="5 4" opacity={0.55} />
        ) : null}

        {series.map((item) => {
          const points = item.months.map((month, i) => `${x(i)},${y(month.closing_cash_satang)}`).join(' ')
          return (
            <g key={item.kind}>
              <polyline points={points} fill="none" stroke={KIND_TONE[item.kind]} strokeWidth={2.1} />
              {item.months.map((month, i) => (
                <circle key={month.month} cx={x(i)} cy={y(month.closing_cash_satang)} r={2} fill={KIND_TONE[item.kind]}>
                  <title>{`${KIND_LABEL[item.kind]} · ${fullMonth(month.month)}: ${money(month.closing_cash_satang)}`}</title>
                </circle>
              ))}
            </g>
          )
        })}

        {series[0]?.months.map((month, i) =>
          i % 2 === 0 || i === monthCount - 1 ? (
            <text key={`x-${month.month}`} x={x(i)} y={H - 13} textAnchor="middle" fontSize={10} fill="var(--color-ivory-dim)">
              {fullMonth(month.month)}
            </text>
          ) : null,
        )}
      </svg>
      <div className="row" style={{ gap: 18, marginTop: 10, fontSize: 11.5 }}>
        {series.map((item) => (
          <span key={item.kind} className="row" style={{ gap: 6 }}>
            <i style={{ width: 16, height: 2, background: KIND_TONE[item.kind], display: 'inline-block' }} />
            <span className="dim">{KIND_LABEL[item.kind]}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

export function Scenarios() {
  const qc = useQueryClient()
  const [form, setForm] = useState(DEFAULT_FORM)
  const [selectedModelID, setSelectedModelID] = useState<string | null>(null)
  const [selectedKind, setSelectedKind] = useState<ScenarioKind>('base')
  const [notice, setNotice] = useState<string | null>(null)

  const runs = useQuery({
    queryKey: ['scenario-runs'],
    queryFn: () => list<ScenarioRun>('/v1/scenarios/runs'),
    staleTime: 30_000,
  })

  const activeModelID = selectedModelID ?? runs.data?.[0]?.model_id

  const bundle = useQuery({
    queryKey: ['scenario-compare', activeModelID],
    queryFn: () => api<ScenarioBundle>(`/v1/scenarios/compare?model_id=${activeModelID}`),
    enabled: Boolean(activeModelID),
    staleTime: 60_000,
  })

  const create = useMutation({
    mutationFn: () => {
      const cash = Number(form.cash)
      const fixed = Number(form.fixed)
      const variable = Number(form.variable)
      const churn = Number(form.churn)
      const expansion = Number(form.expansion)
      return apiWrite<ScenarioBundle>('/v1/scenarios/runs', 'POST', {
        name: form.name.trim() || undefined,
        starting_cash_satang: Math.round(cash * 100),
        fixed_cost_satang: Math.round(fixed * 100),
        variable_cost_bp: Math.round(variable * 100),
        monthly_churn_bp: Math.round(churn * 100),
        monthly_expansion_bp: Math.round(expansion * 100),
        horizon_months: Number(form.horizon),
      })
    },
    onSuccess: (view) => {
      qc.setQueryData(['scenario-compare', view.model.id], view)
      qc.setQueryData<ScenarioRun[]>(['scenario-runs'], (old) => [
        ...view.scenarios.map((item) => item.run),
        ...(old ?? []).filter((run) => run.model_id !== view.model.id),
      ])
      setSelectedModelID(view.model.id)
      setSelectedKind('base')
      setNotice(`สร้างแบบจำลอง ${view.model.name} เมื่อ ${thaiDateTime(view.model.generated_at)} แล้ว`)
      void qc.invalidateQueries({ queryKey: ['scenario-runs'] })
    },
  })

  const cash = Number(form.cash)
  const fixed = Number(form.fixed)
  const variable = Number(form.variable)
  const churn = Number(form.churn)
  const expansion = Number(form.expansion)
  const formValid =
    [cash, fixed, variable, churn, expansion].every((value) => Number.isFinite(value)) &&
    cash >= 0 &&
    fixed >= 0 &&
    variable >= 0 &&
    variable <= 100 &&
    churn >= 0 &&
    churn <= 50 &&
    expansion >= 0 &&
    expansion <= 50

  const data = bundle.data
  const base = scenarioOf(data, 'base')
  const upside = scenarioOf(data, 'upside')
  const downside = scenarioOf(data, 'downside')
  const selected = scenarioOf(data, selectedKind) ?? base
  const runwaySpread =
    base && upside && downside
      ? Math.max(base.run.runway_months, upside.run.runway_months, downside.run.runway_months) -
        Math.min(base.run.runway_months, upside.run.runway_months, downside.run.runway_months)
      : 0
  const sensitivity = useMemo(
    () => (data?.sensitivity ?? []).filter((row) => row.kind === selectedKind),
    [data, selectedKind],
  )
  const busy = create.isPending

  function update(field: keyof typeof DEFAULT_FORM, value: string) {
    setForm((old) => ({ ...old, [field]: value }))
  }

  return (
    <>
      <PageHead
        title="สถานการณ์และกระแสเงินสด"
        sub="ฐานรายได้จากใบแจ้งหนี้ที่ออกแล้วจริง · สามสถานการณ์ฐาน/ดีกว่าคาด/แย่กว่าคาด · ต้นทุนคงที่และผันแปร · runway จนเงินสดติดลบ · ตารางความอ่อนไหว churn ±10%"
        actions={<span className="chip">ประมาณการจากใบแจ้งหนี้จริง</span>}
      />

      {notice ? (
        <div
          role="status"
          style={{
            marginTop: 4,
            marginBottom: 16,
            padding: '10px 14px',
            fontSize: 12.5,
            border: '1px solid var(--color-emerald-soft)',
            borderRadius: 6,
            color: 'var(--color-emerald-soft)',
          }}
        >
          {notice}
        </div>
      ) : null}

      <form
        className="card card-pad"
        onSubmit={(event) => {
          event.preventDefault()
          setNotice(null)
          create.mutate()
        }}
      >
        <div className="row" style={{ alignItems: 'baseline' }}>
          <span className="card-label">สมมติฐานสำหรับการจำลอง</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
            เงินสดและต้นทุนกรอกเป็นบาท · อัตราต่อเดือนกรอกเป็นเปอร์เซ็นต์
          </span>
        </div>
        <div className="grid-3" style={{ marginTop: 4 }}>
          <label className="field">
            <span className="dim" style={{ fontSize: 11.5 }}>ชื่อแบบจำลอง (ไม่บังคับ)</span>
            <input
              className="input"
              value={form.name}
              maxLength={200}
              placeholder="เช่น แผนปี 2570 หลังปิดดีลใหม่"
              onChange={(event) => update('name', event.target.value)}
              disabled={busy}
            />
          </label>
          <label className="field">
            <span className="dim" style={{ fontSize: 11.5 }}>เงินสดเริ่มต้น (บาท)</span>
            <input
              className="input"
              type="number"
              min={0}
              step="0.01"
              value={form.cash}
              onChange={(event) => update('cash', event.target.value)}
              disabled={busy}
              required
            />
          </label>
          <label className="field">
            <span className="dim" style={{ fontSize: 11.5 }}>ต้นทุนคงที่ต่อเดือน (บาท)</span>
            <input
              className="input"
              type="number"
              min={0}
              step="0.01"
              value={form.fixed}
              onChange={(event) => update('fixed', event.target.value)}
              disabled={busy}
              required
            />
          </label>
          <label className="field">
            <span className="dim" style={{ fontSize: 11.5 }}>ต้นทุนผันแปร (% ของรายได้)</span>
            <input
              className="input"
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={form.variable}
              onChange={(event) => update('variable', event.target.value)}
              disabled={busy}
              required
            />
          </label>
          <label className="field">
            <span className="dim" style={{ fontSize: 11.5 }}>Churn ต่อเดือน (%)</span>
            <input
              className="input"
              type="number"
              min={0}
              max={50}
              step="0.01"
              value={form.churn}
              onChange={(event) => update('churn', event.target.value)}
              disabled={busy}
              required
            />
          </label>
          <label className="field">
            <span className="dim" style={{ fontSize: 11.5 }}>Expansion ต่อเดือน (%)</span>
            <input
              className="input"
              type="number"
              min={0}
              max={50}
              step="0.01"
              value={form.expansion}
              onChange={(event) => update('expansion', event.target.value)}
              disabled={busy}
              required
            />
          </label>
          <label className="field">
            <span className="dim" style={{ fontSize: 11.5 }}>จำนวนเดือนที่จำลอง</span>
            <select
              className="input"
              value={form.horizon}
              onChange={(event) => update('horizon', event.target.value)}
              disabled={busy}
            >
              <option value="6">6 เดือน</option>
              <option value="12">12 เดือน</option>
              <option value="24">24 เดือน</option>
              <option value="36">36 เดือน</option>
            </select>
          </label>
        </div>
        <div className="row" style={{ marginTop: 16, gap: 12 }}>
          <button className="btn btn-primary" type="submit" disabled={busy || !formValid}>
            {busy ? 'กำลังจำลอง…' : 'จำลองสามสถานการณ์'}
          </button>
          <span className="dim" style={{ fontSize: 11.5 }}>
            ระบบจะอ่านใบแจ้งหนี้ที่ออกแล้ว 12 เดือนล่าสุด แล้วบันทึก snapshot แยกสำหรับฐาน/ดีกว่าคาด/แย่กว่าคาด
          </span>
        </div>
        {create.isError ? <div className="form-error">{errorText(create.error, 'จำลองสถานการณ์ไม่สำเร็จ')}</div> : null}
      </form>

      {runs.isLoading ? (
        <div className="stack" style={{ marginTop: 16 }}>
          <Skel h={110} />
          <Skel h={280} />
        </div>
      ) : runs.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="form-error">{errorText(runs.error, 'โหลดรายการสถานการณ์ไม่สำเร็จ')}</div>
          <p className="dim" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
            ตรวจสอบว่าเส้นทาง /v1/scenarios/runs ถูก mount แล้ว หรือยังไม่มีใบแจ้งหนี้ที่ออกแล้วในระบบ
          </p>
        </div>
      ) : (runs.data ?? []).length === 0 ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <Empty
            title="ยังไม่มีแบบจำลองสถานการณ์"
            body="กรอกเงินสด ต้นทุน และอัตรา churn/expansion ด้านบน แล้วกดจำลอง ระบบจะสร้างสามสถานการณ์จากใบแจ้งหนี้จริงให้ที่นี่"
          />
        </div>
      ) : bundle.isLoading ? (
        <div className="stack" style={{ marginTop: 16 }}>
          <Skel h={110} />
          <Skel h={280} />
        </div>
      ) : bundle.isError ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <div className="form-error">{errorText(bundle.error, 'โหลดผลเปรียบเทียบสถานการณ์ไม่สำเร็จ')}</div>
        </div>
      ) : !data || !base || !upside || !downside || !selected ? (
        <div className="card card-pad" style={{ marginTop: 16 }}>
          <Empty title="แบบจำลองนี้ไม่มีผลครบสามสถานการณ์" body="ลองสร้างแบบจำลองใหม่ ระบบจะคำนวณฐาน/ดีกว่าคาด/แย่กว่าคาดในครั้งเดียว" />
        </div>
      ) : (
        <>
          <div className="grid-4 stagger" style={{ marginTop: 16 }}>
            <Stat
              label="Runway · ฐาน"
              value={runwayText(base.run)}
              foot={breachText(base.run)}
            />
            <Stat
              label="Runway · ดีกว่าคาด"
              value={runwayText(upside.run)}
              foot={breachText(upside.run)}
            />
            <Stat
              label="Runway · แย่กว่าคาด"
              value={runwayText(downside.run)}
              foot={breachText(downside.run)}
            />
            <Stat
              label="ส่วนต่าง runway"
              value={number(runwaySpread)}
              unit="เดือน"
              foot={`ฐานรายได้ ${money(data.model.base_mrr_satang, { compact: true })} · เงินสดเริ่ม ${money(data.model.starting_cash_satang, { compact: true })}`}
            />
          </div>

          <div className="grid-main" style={{ marginTop: 16 }}>
            <div className="card card-pad">
              <div className="row" style={{ alignItems: 'baseline' }}>
                <span className="card-label">เงินสดคงเหลือรายเดือน</span>
                <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                  {fullMonth(data.model.as_of_month)} เป็นจุดตั้งต้น · จำลอง {number(data.model.horizon_months)} เดือน
                </span>
              </div>
              <div style={{ marginTop: 16 }}>
                <CashChart bundle={data} />
              </div>
            </div>

            <div className="card card-pad">
              <div className="card-label">ฐานข้อมูลจริงที่ใช้</div>
              <dl style={{ margin: '14px 0 0', fontSize: 12, lineHeight: 2 }}>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">หน้าต่างใบแจ้งหนี้</dt>
                  <dd style={{ margin: 0 }}>{fullMonth(data.model.history_from)} – {fullMonth(data.model.history_to)}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">ใบแจ้งหนี้ที่ใช้</dt>
                  <dd style={{ margin: 0 }}>{number(data.model.invoice_count)} ใบ · {number(data.model.months_with_invoices)} เดือน</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">รายได้เฉลี่ยต่อเดือน</dt>
                  <dd style={{ margin: 0 }}>{money(data.model.base_mrr_satang)}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">ยอดใบแจ้งหนี้รวม</dt>
                  <dd style={{ margin: 0 }}>{money(data.model.actual_total_satang)}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">เงินสดเริ่มต้น</dt>
                  <dd style={{ margin: 0 }}>{money(data.model.starting_cash_satang)}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">ต้นทุนคงที่ / ผันแปร</dt>
                  <dd style={{ margin: 0 }}>{money(data.model.fixed_cost_satang)} · {rateLabel(data.model.variable_cost_bp)}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">Churn / Expansion ฐาน</dt>
                  <dd style={{ margin: 0 }}>{rateLabel(data.model.monthly_churn_bp)} · {rateLabel(data.model.monthly_expansion_bp)}</dd>
                </div>
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <dt className="dim">สร้างเมื่อ</dt>
                  <dd style={{ margin: 0 }}>{thaiDateTime(data.model.generated_at)}</dd>
                </div>
              </dl>
              <p className="dim" style={{ fontSize: 11.5, lineHeight: 1.8, marginTop: 14, marginBottom: 0 }}>
                ตัวคูณของแต่ละสถานการณ์: {KIND_NOTE.base} · {KIND_NOTE.upside} · {KIND_NOTE.downside}
              </p>
            </div>
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">เปรียบเทียบสามสถานการณ์</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                คลิกแถวเพื่อดูกระแสเงินสดรายเดือน · runway คือจำนวนเดือนเต็มที่เงินสดยังไม่ติดลบ
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 14 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>สถานการณ์</th>
                    <th style={{ textAlign: 'right' }}>รายได้เริ่มต้น</th>
                    <th style={{ textAlign: 'right' }}>Churn / เดือน</th>
                    <th style={{ textAlign: 'right' }}>Expansion / เดือน</th>
                    <th style={{ textAlign: 'right' }}>รายได้รวม</th>
                    <th style={{ textAlign: 'right' }}>ต้นทุนรวม</th>
                    <th style={{ textAlign: 'right' }}>กระแสเงินสดสุทธิ</th>
                    <th style={{ textAlign: 'right' }}>Runway</th>
                    <th style={{ textAlign: 'right' }}>เงินสดสิ้นช่วง</th>
                  </tr>
                </thead>
                <tbody>
                  {KIND_ORDER.map((kind) => {
                    const item = scenarioOf(data, kind)
                    if (!item) return null
                    return (
                      <tr
                        key={kind}
                        onClick={() => setSelectedKind(kind)}
                        style={{
                          cursor: 'pointer',
                          background: kind === selectedKind ? 'var(--color-wash)' : undefined,
                        }}
                      >
                        <td>
                          <span className="row" style={{ gap: 8 }}>
                            <i style={{ width: 8, height: 8, borderRadius: '50%', background: KIND_TONE[kind], display: 'inline-block' }} />
                            {KIND_LABEL[kind]}
                            {kind === selectedKind ? <span className="chip">กำลังดู</span> : null}
                          </span>
                        </td>
                        <td className="num tnum">{money(item.run.starting_revenue_satang)}</td>
                        <td className="num tnum">{rateLabel(item.run.effective_churn_bp)}</td>
                        <td className="num tnum">{rateLabel(item.run.effective_expansion_bp)}</td>
                        <td className="num tnum">{money(item.run.total_revenue_satang, { compact: true })}</td>
                        <td className="num tnum">{money(item.run.total_cost_satang, { compact: true })}</td>
                        <td className={`num tnum ${item.run.total_net_cashflow_satang < 0 ? 'sev-critical' : 'sev-low'}`}>
                          {money(item.run.total_net_cashflow_satang, { compact: true })}
                        </td>
                        <td className="num tnum">{runwayText(item.run)}</td>
                        <td className={`num tnum ${item.run.ending_cash_satang < 0 ? 'sev-critical' : ''}`}>
                          {money(item.run.ending_cash_satang, { compact: true })}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">กระแสเงินสดรายเดือน · {KIND_LABEL[selectedKind]}</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                เงินสดติดลบครั้งแรก: {breachText(selected.run)}
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 14 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>เดือน</th>
                    <th style={{ textAlign: 'right' }}>รายได้</th>
                    <th style={{ textAlign: 'right' }}>ต้นทุนคงที่</th>
                    <th style={{ textAlign: 'right' }}>ต้นทุนผันแปร</th>
                    <th style={{ textAlign: 'right' }}>รวมต้นทุน</th>
                    <th style={{ textAlign: 'right' }}>กระแสเงินสดสุทธิ</th>
                    <th style={{ textAlign: 'right' }}>เงินสดต้นเดือน</th>
                    <th style={{ textAlign: 'right' }}>เงินสดปลายเดือน</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.months.map((month) => (
                    <tr key={month.month_index} style={{ cursor: 'default' }}>
                      <td>{fullMonth(month.month)}</td>
                      <td className="num tnum">{money(month.revenue_satang)}</td>
                      <td className="num tnum dim">{money(month.fixed_cost_satang)}</td>
                      <td className="num tnum dim">{money(month.variable_cost_satang)}</td>
                      <td className="num tnum">{money(month.total_cost_satang)}</td>
                      <td className={`num tnum ${month.net_cashflow_satang < 0 ? 'sev-critical' : 'sev-low'}`}>
                        {money(month.net_cashflow_satang)}
                      </td>
                      <td className="num tnum dim">{money(month.opening_cash_satang)}</td>
                      <td className={`num tnum ${month.closing_cash_satang < 0 ? 'sev-critical' : ''}`}>
                        {money(month.closing_cash_satang)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid-2" style={{ marginTop: 16, alignItems: 'start' }}>
            <div className="card card-pad">
              <div className="row" style={{ alignItems: 'baseline' }}>
                <span className="card-label">ความอ่อนไหว churn ±10% · {KIND_LABEL[selectedKind]}</span>
                <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                  เปลี่ยนเฉพาะอัตรา churn ของสถานการณ์นี้
                </span>
              </div>
              <div className="table-wrap" style={{ marginTop: 14 }}>
                <table className="table data">
                  <thead>
                    <tr>
                      <th>การปรับ</th>
                      <th style={{ textAlign: 'right' }}>Churn / เดือน</th>
                      <th style={{ textAlign: 'right' }}>Runway</th>
                      <th style={{ textAlign: 'right' }}>เงินสดสิ้นช่วง</th>
                      <th style={{ textAlign: 'right' }}>กระแสเงินสดเดือนแรก</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sensitivity.map((row) => (
                      <tr key={`${row.kind}-${row.churn_delta_bp}`} style={{ cursor: 'default' }}>
                        <td>
                          {deltaLabel(row.churn_delta_bp)}
                          {row.churn_delta_bp === 0 ? <span className="chip" style={{ marginLeft: 8 }}>ฐาน</span> : null}
                        </td>
                        <td className="num tnum">{rateLabel(row.effective_churn_bp)}</td>
                        <td className="num tnum">
                          {row.runway_beyond_horizon ? `เกิน ${number(data.model.horizon_months)} เดือน` : `${number(row.runway_months)} เดือน`}
                        </td>
                        <td className={`num tnum ${row.ending_cash_satang < 0 ? 'sev-critical' : ''}`}>
                          {money(row.ending_cash_satang, { compact: true })}
                        </td>
                        <td className={`num tnum ${row.first_month_net_satang < 0 ? 'sev-critical' : 'sev-low'}`}>
                          {money(row.first_month_net_satang, { compact: true })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card card-pad">
              <div className="card-label">ข้อจำกัดที่ควรรู้</div>
              <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
                <li>รายได้ฐานคือค่าเฉลี่ยต่อเดือนที่มีใบแจ้งหนี้จริง ไม่ใช่ยอดที่ผู้ใช้พิมพ์เอง และไม่ใช่รายได้ที่รับเงินแล้ว</li>
                <li>กระแสเงินสดเป็นเกณฑ์คงค้างจากใบแจ้งหนี้ ไม่ได้หักหนี้สูญ ส่วนลด หรือระยะเวลาชำระจริง</li>
                <li>churn และ expansion ใช้แบบทบต้นทุกเดือนจากยอดที่ปัดเป็นสตางค์แล้ว จึงตรวจย้อนกลับได้ด้วยมือ</li>
                <li>ตัวคูณสามสถานการณ์เป็นค่าคงที่ของระบบ ไม่รับค่าจากผู้ใช้ เพื่อให้ความหมายของ "ฐาน" และ "แย่กว่าคาด" คงเดิม</li>
                <li>runway ไม่ได้แปลว่าธุรกิจหยุดเมื่อเงินสดติดลบ เป็นเพียงจุดที่ต้องระดมทุนหรือลดต้นทุนก่อนถึงเดือนนั้น</li>
              </ul>
            </div>
          </div>

          <div className="card card-pad" style={{ marginTop: 16 }}>
            <div className="row" style={{ alignItems: 'baseline' }}>
              <span className="card-label">ประวัติแบบจำลอง</span>
              <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>
                แต่ละครั้งเป็น snapshot ที่ไม่ถูกแก้ย้อนหลัง
              </span>
            </div>
            <div className="table-wrap" style={{ marginTop: 14 }}>
              <table className="table data">
                <thead>
                  <tr>
                    <th>แบบจำลอง</th>
                    <th>สถานการณ์</th>
                    <th>สร้างเมื่อ</th>
                    <th style={{ textAlign: 'right' }}>รายได้เริ่มต้น</th>
                    <th style={{ textAlign: 'right' }}>Runway</th>
                    <th style={{ textAlign: 'right' }}>Churn</th>
                    <th style={{ textAlign: 'right' }}>เดือน</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(runs.data ?? []).map((run) => (
                    <tr key={run.id} style={{ cursor: 'default' }}>
                      <td>
                        {run.model_name}
                        <span className="dim mono" style={{ marginLeft: 8, fontSize: 10.5 }}>{shortID(run.model_id)}</span>
                      </td>
                      <td>
                        <span className="row" style={{ gap: 7 }}>
                          <i style={{ width: 7, height: 7, borderRadius: '50%', background: KIND_TONE[run.kind], display: 'inline-block' }} />
                          {KIND_LABEL[run.kind]}
                        </span>
                      </td>
                      <td className="dim">{thaiDateTime(run.generated_at)}</td>
                      <td className="num tnum">{money(run.starting_revenue_satang, { compact: true })}</td>
                      <td className="num tnum">{runwayText(run)}</td>
                      <td className="num tnum">{rateLabel(run.effective_churn_bp)}</td>
                      <td className="num tnum">{number(run.horizon_months)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          type="button"
                          onClick={() => {
                            setSelectedModelID(run.model_id)
                            setSelectedKind(run.kind)
                          }}
                          disabled={run.model_id === data.model.id && run.kind === selectedKind}
                        >
                          {run.model_id === data.model.id && run.kind === selectedKind ? 'กำลังแสดง' : 'เปิด'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  )
}
